/**
 * Copyright (c) 2021-2025 Kognitos, Inc. All rights reserved.
 */

/**
 * Runtime configuration injected into window.__RUNTIME_CONFIG__.
 *
 * - Development: Vite plugin fetches from AWS SSM and injects via transformIndexHtml
 * - Production: nginx sub_filter injects at container startup from environment variables
 *
 * This enables "build once, deploy everywhere" by separating config from the build.
 */
export interface RuntimeConfig {
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
  auth0CustomAudience?: string;
  sentryDsn?: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
    __ENV__?: {
      E2E_TEST_MODE?: string;
    };
  }
}

/**
 * Get runtime configuration.
 *
 * Config is injected by Vite (dev) or nginx (production).
 * In demo/E2E mode or when config is missing, returns minimal config without Auth0 values.
 *
 * This ensures the app always works, even without proper config injection.
 */
export function getRuntimeConfig(): RuntimeConfig {
  const config = window.__RUNTIME_CONFIG__;

  // If config exists, use it
  if (config) {
    return config;
  }

  // No config found - default to demo mode config to avoid errors
  // This allows the app to work without proper config injection
  console.warn(
    'Runtime configuration not found. Using demo mode config. ' +
      'Set VITE_E2E_TEST_MODE=true for demo mode, or inject config via nginx/docker-entrypoint.sh',
  );

  return {
    auth0Domain: 'demo-mode',
    auth0ClientId: 'demo-mode',
    auth0Audience: 'demo-mode',
    sentryDsn: undefined,
  };
}
